import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { ReportViewStore } from "../../store";
import { ReportView } from "./ReportView";
import { ReportUpdateStore } from "../../store";
import { ReportUpdate } from "../update/ReportUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type ReportDrawerContentProps = {
  entityId: string;
  mode?: "view" | "update";
};

export const ReportDrawerContent: React.FC<ReportDrawerContentProps> = observer(
  ({ entityId, mode = "view" }) => {
    const rootStore = useRootStore();
    const viewInitializedRef = useRef<string | null>(null);
    const updateInitializedRef = useRef<string | null>(null);

    const viewStore = useMemo(() => {
      const s = new ReportViewStore(rootStore);
      s.isDrawerMode = true;
      return s;
    }, [rootStore]);

    useEffect(() => {
      if (
        mode === "view" &&
        entityId &&
        viewInitializedRef.current !== entityId
      ) {
        viewInitializedRef.current = entityId;
        viewStore.initialize({ id: entityId });
      }
    }, [entityId, mode, viewStore]);

    const updateStore = useMemo(() => {
      const s = new ReportUpdateStore(rootStore);
      s.isDrawerMode = true;
      return s;
    }, [rootStore]);

    useEffect(() => {
      if (
        mode === "update" &&
        entityId &&
        updateInitializedRef.current !== entityId
      ) {
        updateInitializedRef.current = entityId;
        updateStore.initialize({ id: entityId });
      }
    }, [entityId, mode, updateStore]);

    if (mode === "update") {
      if (
        updateStore.status === "processing" ||
        updateStore.status === "uninitialized"
      ) {
        return (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 200,
            }}
          >
            <CircularProgress />
          </Box>
        );
      }
      return <ReportUpdate reportUpdateStore={updateStore} />;
    }

    if (
      viewStore.status === "processing" ||
      viewStore.status === "uninitialized"
    ) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return <ReportView reportViewStore={viewStore} />;
  }
);
